import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBirthdayCake,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronDown,
  FaEnvelope,
  FaEuroSign,
  FaIdCard,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhoneAlt,
  FaRegAddressCard,
  FaShieldAlt,
  FaSpinner,
  FaUser,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/Reveal/Reveal";
import ScrollIndicator from "../../../components/ScrollIndicator/ScrollIndicator";

import Footer from "../HomePage/Footer";

import { addNewAdherent } from "../../../services/adherentServices";
import { getGroupsHome } from "../../../services/groupServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";

import "react-toastify/dist/ReactToastify.css";
import "../HomePage/Home.css";

const INITIAL_FORM = {
  adherent_groupe: "",
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "M",
  date_naissance: "",
  poids: "",
  licence_number: "",
  autorisation_photo: true,
  notes_adherent: "",
};

const STEPS = [
  {
    id: 1,
    label: "Adhérent",
    icon: FaUser,
  },
  {
    id: 2,
    label: "Coordonnées",
    icon: FaRegAddressCard,
  },
  {
    id: 3,
    label: "Groupe",
    icon: FaUsers,
  },
  {
    id: 4,
    label: "Validation",
    icon: FaCheckCircle,
  },
];

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary focus:bg-white focus:ring-4 focus:ring-orange-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary";

const labelClass =
  "mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200";

const errorClass = "mt-2 text-xs font-semibold text-red-600 dark:text-red-400";

const onlyDigits = (value = "") => String(value).replace(/\D+/g, "");

const normalizePhoneFR = (value = "") => {
  const digits = onlyDigits(value);

  if (digits.startsWith("33")) {
    return `0${digits.slice(2, 11)}`;
  }

  if (digits.startsWith("0")) {
    return digits.slice(0, 10);
  }

  return digits.slice(0, 10);
};

const normalizeName = (value = "") =>
  String(value).replace(/\s+/g, " ").replace(/^\s/, "");

const normalizeWeightInput = (value = "") =>
  String(value)
    .replace(",", ".")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1");

const computeAge = (dateStr) => {
  if (!dateStr) return null;

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();

  const monthDiff = today.getMonth() - date.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age -= 1;
  }

  return Math.max(0, age);
};

const getBirthYear = (dateStr) => {
  const date = dateStr ? new Date(dateStr) : null;

  if (!date || Number.isNaN(date.getTime())) return null;

  return date.getFullYear();
};

const formatDateDashFR = (dateStr) => {
  if (!dateStr) return "—";

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return dateStr;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const isActiveGroup = (group) => {
  if (
    group?.is_active === undefined ||
    group?.is_active === null ||
    group?.is_active === ""
  ) {
    return true;
  }

  return (
    group.is_active === true || group.is_active === 1 || group.is_active === "1"
  );
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const normalized = String(value)
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

  const amount = Number.parseFloat(normalized);

  return Number.isFinite(amount) ? amount : null;
};

const formatEuro = (value) => {
  if (!Number.isFinite(value)) return "—";

  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const getGroupTeachers = (group) =>
  [group?.enseignant1, group?.enseignant2].filter(Boolean).join(" & ");

const getGroupSchedule = (group) =>
  [group?.horaire1, group?.horaire2].filter(Boolean);

const getGroupTotal = (group) => {
  const licence = parseAmount(group?.tarif_licence);
  const cotisation = parseAmount(group?.tarif_cotisation);

  if (!Number.isFinite(licence) && !Number.isFinite(cotisation)) return null;

  return Number(licence || 0) + Number(cotisation || 0);
};

function FieldError({ message }) {
  if (!message) return null;

  return <p className={errorClass}>{message}</p>;
}

function SectionTitle({ icon: Icon, title, text }) {
  return (
    <div className="mb-7">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <h3 className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      {text && (
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          {text}
        </p>
      )}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="-mx-1 overflow-x-auto px-1 pb-2">
      <div className="grid min-w-[720px] grid-cols-4 gap-3 md:min-w-0">
        {STEPS.map((item) => {
          const Icon = item.icon;
          const isActive = step === item.id;
          const isDone = step > item.id;

          return (
            <div
              key={item.id}
              className={`rounded-2xl border p-4 transition ${
                isActive
                  ? "border-orange-200 bg-orange-50 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10"
                  : isDone
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10"
                    : "border-slate-200 bg-white dark:border-white/10 dark:bg-white/5"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                    isActive
                      ? "bg-primary text-white"
                      : isDone
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-100 text-slate-400 dark:bg-white/10"
                  }`}
                >
                  {isDone ? <FaCheckCircle /> : <Icon />}
                </div>

                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Étape {item.id}
                  </p>
                  <p
                    className={`truncate text-sm font-bold ${
                      isActive
                        ? "text-primary"
                        : "text-slate-700 dark:text-slate-200"
                    }`}
                  >
                    {item.label}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FormInput({
  label,
  name,
  value,
  onChange,
  error,
  type = "text",
  placeholder = "",
  required = false,
  disabled = false,
  inputMode,
  autoComplete,
}) {
  return (
    <div>
      <label htmlFor={name} className={labelClass}>
        {label} {required && <span className="text-primary">*</span>}
      </label>

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        inputMode={inputMode}
        autoComplete={autoComplete}
        className={inputClass}
      />

      <FieldError message={error} />
    </div>
  );
}

function GenderChoice({ value, onChange, error }) {
  const choices = [
    {
      value: "M",
      label: "Masculin",
    },
    {
      value: "F",
      label: "Féminin",
    },
  ];

  return (
    <div>
      <label className={labelClass}>
        Genre <span className="text-primary">*</span>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        {choices.map((choice) => {
          const active = value === choice.value;

          return (
            <label
              key={choice.value}
              className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 text-sm font-bold transition ${
                active
                  ? "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:border-orange-200 hover:bg-orange-50/50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              }`}
            >
              <input
                type="radio"
                name="genre"
                value={choice.value}
                checked={active}
                onChange={onChange}
                className="sr-only"
              />

              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                  active
                    ? "border-primary bg-primary"
                    : "border-slate-300 dark:border-white/20"
                }`}
              >
                {active && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>

              {choice.label}
            </label>
          );
        })}
      </div>

      <FieldError message={error} />
    </div>
  );
}

function SelectedGroupCard({ group }) {
  if (!group) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-white/10 dark:bg-white/5">
        <FaUsers className="mx-auto mb-4 text-3xl text-primary" />
        <p className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
          Aucun groupe sélectionné
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          Sélectionnez un groupe pour consulter ses horaires, enseignants et
          tarifs.
        </p>
      </div>
    );
  }

  const schedule = getGroupSchedule(group);
  const teachers = getGroupTeachers(group);
  const licence = parseAmount(group.tarif_licence);
  const cotisation = parseAmount(group.tarif_cotisation);
  const total = getGroupTotal(group);

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="bg-slate-950 p-5 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
          Groupe sélectionné
        </p>

        <h4 className="mt-2 font-barlow text-3xl font-bold">{group.nom}</h4>

        {group.annees_naissance && (
          <p className="mt-2 text-sm font-semibold text-white/70">
            {group.annees_naissance}
          </p>
        )}
      </div>

      <div className="space-y-4 p-5">
        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            <FaCalendarAlt className="text-primary" />
            Horaires
          </p>

          {schedule.length > 0 ? (
            <div className="space-y-2">
              {schedule.map((item, index) => (
                <p
                  key={`${item}-${index}`}
                  className="rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600 dark:bg-white/5 dark:text-slate-300"
                >
                  {item}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Horaires non renseignés.</p>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            Enseignant(s)
          </p>

          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
            {teachers || "Non renseigné"}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Licence
            </p>
            <p className="mt-1 font-barlow text-xl font-bold text-slate-950 dark:text-white">
              {formatEuro(licence)}
            </p>
          </div>

          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Cotisation
            </p>
            <p className="mt-1 font-barlow text-xl font-bold text-slate-950 dark:text-white">
              {formatEuro(cotisation)}
            </p>
          </div>

          <div className="rounded-2xl bg-orange-50 p-4 text-orange-900 dark:bg-orange-500/10 dark:text-orange-200">
            <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-70">
              Total
            </p>
            <p className="mt-1 font-barlow text-xl font-bold">
              {formatEuro(total)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepIdentity({ formData, errors, onChange, age, birthYear, onNext }) {
  return (
    <div>
      <SectionTitle
        icon={FaIdCard}
        title="Informations de l’adhérent"
        text="Renseignez l’identité de la personne qui souhaite s’inscrire au club."
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <FormInput
          label="Nom"
          name="nom"
          value={formData.nom}
          onChange={onChange}
          error={errors.nom}
          placeholder="DUPONT"
          required
          autoComplete="family-name"
        />

        <FormInput
          label="Prénom"
          name="prenom"
          value={formData.prenom}
          onChange={onChange}
          error={errors.prenom}
          placeholder="Marie"
          required
          autoComplete="given-name"
        />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <GenderChoice
          value={formData.genre}
          onChange={onChange}
          error={errors.genre}
        />

        <div>
          <FormInput
            label="Date de naissance"
            name="date_naissance"
            type="date"
            value={formData.date_naissance}
            onChange={onChange}
            error={errors.date_naissance}
            required
          />

          {(age !== null || birthYear) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {age !== null && (
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1.5 text-xs font-bold text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaBirthdayCake />
                  Âge estimé : {age} ans
                </span>
              )}

              {birthYear && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  Année : {birthYear}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
        >
          Continuer
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
}

function StepContact({ formData, errors, onChange, onPrevious, onNext }) {
  return (
    <div>
      <SectionTitle
        icon={FaMapMarkerAlt}
        title="Coordonnées"
        text="Ces informations permettront au club de vous contacter au sujet de l’inscription."
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={onChange}
          error={errors.email}
          placeholder="exemple@mail.com"
          required
          autoComplete="email"
        />

        <FormInput
          label="Email secondaire"
          name="email_secondaire"
          type="email"
          value={formData.email_secondaire}
          onChange={onChange}
          error={errors.email_secondaire}
          placeholder="parent@mail.com"
          autoComplete="email"
        />

        <FormInput
          label="Téléphone"
          name="tel"
          type="tel"
          value={formData.tel}
          onChange={onChange}
          error={errors.tel}
          placeholder="06XXXXXXXX"
          required
          inputMode="numeric"
          autoComplete="tel"
        />

        <FormInput
          label="Téléphone secondaire"
          name="tel_secondaire"
          type="tel"
          value={formData.tel_secondaire}
          onChange={onChange}
          error={errors.tel_secondaire}
          placeholder="06XXXXXXXX"
          inputMode="numeric"
          autoComplete="tel"
        />
      </div>

      <div className="mt-5">
        <FormInput
          label="Adresse"
          name="adresse"
          value={formData.adresse}
          onChange={onChange}
          error={errors.adresse}
          placeholder="12 rue des Lilas"
          required
          autoComplete="street-address"
        />
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-[0.45fr_1fr]">
        <FormInput
          label="Code postal"
          name="cp"
          value={formData.cp}
          onChange={onChange}
          error={errors.cp}
          placeholder="33000"
          required
          inputMode="numeric"
          autoComplete="postal-code"
        />

        <FormInput
          label="Commune"
          name="commune"
          value={formData.commune}
          onChange={onChange}
          error={errors.commune}
          placeholder="Bordeaux"
          required
          autoComplete="address-level2"
        />
      </div>

      <div className="mt-8 flex justify-between gap-3">
        <button
          type="button"
          onClick={onPrevious}
          className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <FaArrowLeft />
          Retour
        </button>

        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
        >
          Continuer
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
}

function StepGroup({
  formData,
  errors,
  onChange,
  onGroupChange,
  groups,
  loadingGroups,
  selectedGroup,
  onPrevious,
  onNext,
}) {
  return (
    <div>
      <SectionTitle
        icon={FaUsers}
        title="Groupe & options"
        text="Sélectionnez le groupe souhaité, puis complétez les éventuelles informations utiles."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr]">
        <div className="space-y-5">
          <div className="relative">
            <label htmlFor="adherent_groupe" className={labelClass}>
              Groupe <span className="text-primary">*</span>
            </label>

            <select
              id="adherent_groupe"
              name="adherent_groupe"
              value={formData.adherent_groupe}
              onChange={onGroupChange}
              disabled={loadingGroups}
              className={`${inputClass} appearance-none pr-11`}
            >
              <option value="">
                {loadingGroups
                  ? "Chargement des groupes..."
                  : "Sélectionnez un groupe"}
              </option>

              {groups.map((group) => (
                <option key={group.id} value={group.nom}>
                  {group.nom}
                  {group.annees_naissance ? ` — ${group.annees_naissance}` : ""}
                </option>
              ))}
            </select>

            <FaChevronDown className="pointer-events-none absolute bottom-4 right-4 text-xs text-slate-400" />

            <FieldError message={errors.adherent_groupe} />
          </div>

          <div className="grid gap-5 sm:grid-cols-[0.45fr_1fr]">
            <FormInput
              label="Poids"
              name="poids"
              value={formData.poids}
              onChange={onChange}
              error={errors.poids}
              placeholder="Ex : 42.5"
              inputMode="decimal"
            />

            <FormInput
              label="N° de licence"
              name="licence_number"
              value={formData.licence_number}
              onChange={onChange}
              error={errors.licence_number}
              placeholder="Laisser vide si débutant"
            />
          </div>

          <label className="flex cursor-pointer items-start gap-4 rounded-[2rem] border border-slate-200 bg-slate-50 p-5 transition hover:border-orange-200 hover:bg-orange-50/40 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10">
            <input
              type="checkbox"
              name="autorisation_photo"
              checked={formData.autorisation_photo}
              onChange={onChange}
              className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
            />

            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Autorisation photo
              </p>
              <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                J’autorise le club à prendre des photos pendant les activités,
                dans le cadre de sa communication.
              </p>
            </div>
          </label>

          <div>
            <label htmlFor="notes_adherent" className={labelClass}>
              Notes complémentaires
            </label>

            <textarea
              id="notes_adherent"
              name="notes_adherent"
              value={formData.notes_adherent}
              onChange={onChange}
              rows="5"
              placeholder="Allergies, remarques, informations utiles..."
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>

        <SelectedGroupCard group={selectedGroup} />
      </div>

      <div className="mt-8 flex justify-between gap-3">
        <button
          type="button"
          onClick={onPrevious}
          className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <FaArrowLeft />
          Retour
        </button>

        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
        >
          Vérifier
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-bold text-slate-800 dark:text-slate-100">
        {value || "—"}
      </p>
    </div>
  );
}

function StepSummary({
  formData,
  selectedGroup,
  loadingSubmit,
  onPrevious,
  onSubmit,
}) {
  return (
    <div>
      <SectionTitle
        icon={FaShieldAlt}
        title="Vérification"
        text="Relisez les informations avant d’envoyer la demande d’inscription au club."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
        <div className="space-y-5">
          <div>
            <h4 className="mb-3 font-barlow text-2xl font-bold text-slate-950 dark:text-white">
              Adhérent
            </h4>

            <div className="grid gap-3 sm:grid-cols-2">
              <SummaryLine label="Nom" value={formData.nom} />
              <SummaryLine label="Prénom" value={formData.prenom} />
              <SummaryLine label="Genre" value={formData.genre} />
              <SummaryLine
                label="Naissance"
                value={formatDateDashFR(formData.date_naissance)}
              />
            </div>
          </div>

          <div>
            <h4 className="mb-3 font-barlow text-2xl font-bold text-slate-950 dark:text-white">
              Coordonnées
            </h4>

            <div className="grid gap-3 sm:grid-cols-2">
              <SummaryLine label="Email" value={formData.email} />
              <SummaryLine label="Téléphone" value={formData.tel} />
              <SummaryLine label="Adresse" value={formData.adresse} />
              <SummaryLine
                label="Commune"
                value={`${formData.cp || ""} ${formData.commune || ""}`.trim()}
              />
            </div>
          </div>

          <div>
            <h4 className="mb-3 font-barlow text-2xl font-bold text-slate-950 dark:text-white">
              Options
            </h4>

            <div className="grid gap-3 sm:grid-cols-2">
              <SummaryLine
                label="Poids"
                value={formData.poids ? `${formData.poids} kg` : "—"}
              />
              <SummaryLine
                label="Licence"
                value={formData.licence_number || "Débutant / non renseigné"}
              />
              <SummaryLine
                label="Autorisation photo"
                value={formData.autorisation_photo ? "Oui" : "Non"}
              />
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Notes
            </p>
            <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
              {formData.notes_adherent || "Aucune note complémentaire."}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <SelectedGroupCard group={selectedGroup} />

          <div className="rounded-[2rem] border border-emerald-200 bg-emerald-50 p-5 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
            <div className="flex items-start gap-3">
              <FaCheckCircle className="mt-1 shrink-0" />
              <p className="text-sm font-semibold leading-6">
                La demande sera transmise au club. Vous serez recontacté si des
                informations complémentaires sont nécessaires.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col justify-between gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onPrevious}
          disabled={loadingSubmit}
          className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <FaArrowLeft />
          Retour
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={loadingSubmit}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white shadow-[0_18px_45px_rgba(242,113,49,0.30)] transition hover:-translate-y-0.5 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loadingSubmit ? (
            <>
              <FaSpinner className="animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <FaPaperPlane />
              Envoyer l’inscription
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default function InscriptionPage() {
  useScrollToTop();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);

  const formCardRef = useRef(null);

  const scrollToForm = () => {
    window.setTimeout(() => {
      if (!formCardRef.current) return;

      const navbar = document.querySelector("header");
      const navbarHeight = navbar?.offsetHeight || 92;

      const extraSpacing = 24;
      const offset = navbarHeight + extraSpacing;

      const elementTop =
        formCardRef.current.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: elementTop - offset,
        behavior: "smooth",
      });
    }, 120);
  };

  useEffect(() => {
    let mounted = true;

    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);

        const groupsData = await getGroupsHome();

        if (!mounted) return;

        const activeGroups = (Array.isArray(groupsData) ? groupsData : [])
          .filter(isActiveGroup)
          .sort((a, b) => {
            const orderA = Number(a?.ordre || a?.order || 999);
            const orderB = Number(b?.ordre || b?.order || 999);

            return orderA - orderB;
          });

        setGroups(activeGroups);
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes :", error);
        toast.error("Impossible de charger les groupes.");
      } finally {
        if (mounted) {
          setLoadingGroups(false);
        }
      }
    };

    fetchGroups();

    return () => {
      mounted = false;
    };
  }, []);

  const birthYear = useMemo(() => {
    return getBirthYear(formData.date_naissance);
  }, [formData.date_naissance]);

  const age = useMemo(() => {
    return computeAge(formData.date_naissance);
  }, [formData.date_naissance]);

  const selectedGroup = useMemo(() => {
    return (
      groups.find((group) => group.nom === formData.adherent_groupe) || null
    );
  }, [groups, formData.adherent_groupe]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    let nextValue = type === "checkbox" ? checked : value;

    if (name === "nom") {
      nextValue = normalizeName(nextValue).toUpperCase();
    }

    if (name === "prenom") {
      nextValue = normalizeName(nextValue);
    }

    if (name === "tel" || name === "tel_secondaire") {
      nextValue = normalizePhoneFR(nextValue);
    }

    if (name === "cp") {
      nextValue = onlyDigits(nextValue).slice(0, 5);
    }

    if (name === "poids") {
      nextValue = normalizeWeightInput(nextValue);
    }

    if (name === "licence_number") {
      nextValue = String(nextValue).toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleGroupChange = (event) => {
    handleChange(event);
  };

  const getStepErrors = (targetStep) => {
    const nextErrors = {};

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const postalCodePattern = /^[0-9]{5}$/;
    const phonePattern = /^0\d{9}$/;

    const isDateValid = (dateString) => {
      const date = new Date(dateString);

      if (Number.isNaN(date.getTime())) return false;

      const today = new Date();
      const minDate = new Date(1900, 0, 1);

      return date < today && date >= minDate;
    };

    if (targetStep === 1) {
      if (!formData.nom.trim()) nextErrors.nom = "Nom requis.";
      if (!formData.prenom.trim()) nextErrors.prenom = "Prénom requis.";

      if (!formData.date_naissance) {
        nextErrors.date_naissance = "Date de naissance requise.";
      } else if (!isDateValid(formData.date_naissance)) {
        nextErrors.date_naissance = "Date invalide.";
      }

      if (!formData.genre) {
        nextErrors.genre = "Choix requis.";
      }
    }

    if (targetStep === 2) {
      if (!formData.email || !emailPattern.test(formData.email)) {
        nextErrors.email = "Email valide requis.";
      }

      if (
        formData.email_secondaire &&
        !emailPattern.test(formData.email_secondaire)
      ) {
        nextErrors.email_secondaire = "Email secondaire invalide.";
      }

      if (!formData.tel || !phonePattern.test(formData.tel)) {
        nextErrors.tel = "Téléphone français requis : 0 + 9 chiffres.";
      }

      if (
        formData.tel_secondaire &&
        !phonePattern.test(formData.tel_secondaire)
      ) {
        nextErrors.tel_secondaire =
          "Téléphone secondaire invalide : 0 + 9 chiffres.";
      }

      if (!formData.adresse.trim()) {
        nextErrors.adresse = "Adresse requise.";
      }

      if (!formData.cp || !postalCodePattern.test(formData.cp)) {
        nextErrors.cp = "Code postal requis : 5 chiffres.";
      }

      if (!formData.commune.trim()) {
        nextErrors.commune = "Commune requise.";
      }
    }

    if (targetStep === 3) {
      if (!formData.adherent_groupe) {
        nextErrors.adherent_groupe = "Sélectionnez un groupe.";
      }
    }

    return nextErrors;
  };

  const validateStep = (targetStep) => {
    const nextErrors = getStepErrors(targetStep);

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const validateAll = () => {
    const step1Errors = getStepErrors(1);
    const step2Errors = getStepErrors(2);
    const step3Errors = getStepErrors(3);

    const allErrors = {
      ...step1Errors,
      ...step2Errors,
      ...step3Errors,
    };

    setErrors(allErrors);

    if (Object.keys(step1Errors).length > 0) {
      setStep(1);
      return false;
    }

    if (Object.keys(step2Errors).length > 0) {
      setStep(2);
      return false;
    }

    if (Object.keys(step3Errors).length > 0) {
      setStep(3);
      return false;
    }

    return true;
  };

  const goToNextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(4, prev + 1));
      scrollToForm();
    }
  };

  const goToPreviousStep = () => {
    setStep((prev) => Math.max(1, prev - 1));
    scrollToForm();
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      toast.error("Veuillez corriger les erreurs du formulaire.");
      return;
    }

    const payload = {
      ...formData,
      poids: formData.poids ? String(formData.poids).replace(",", ".") : "",
      nom: formData.nom.trim(),
      prenom: formData.prenom.trim(),
      email: formData.email.trim(),
      email_secondaire: formData.email_secondaire.trim(),
      adresse: formData.adresse.trim(),
      commune: formData.commune.trim(),
      licence_number: formData.licence_number.trim(),
      notes_adherent: formData.notes_adherent.trim(),
    };

    setLoadingSubmit(true);

    try {
      await addNewAdherent(payload);

      const notificationMessage = `Nouvelle inscription : ${payload.nom} ${payload.prenom} • Groupe : ${payload.adherent_groupe}`;

      try {
        await sendNotificationToAdmin(notificationMessage);
      } catch (notificationError) {
        console.error(
          "Inscription enregistrée, mais notification non envoyée :",
          notificationError,
        );
      }

      toast.success("Inscription enregistrée avec succès.");
      resetForm();
    } catch (error) {
      console.error("Erreur lors de l’ajout de l’adhérent :", error);
      toast.error("Erreur lors de l’enregistrement de l’inscription.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto max-w-7xl">
            <Reveal className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                Inscription
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Rejoindre le club
                <br />
                en quelques étapes
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Complétez le formulaire pour transmettre votre demande
                d’inscription. Le club pourra ensuite reprendre contact avec
                vous.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <Reveal className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <FaUserPlus />
                Demande d’inscription
              </div>

              <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Inscription <span className="text-primary">des adhérents</span>
              </h2>

              <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
                Les informations saisies permettront au club de préparer votre
                adhésion, de proposer le bon groupe et de vous recontacter.
              </p>
            </Reveal>

            <Reveal
              variant="zoom"
              delay={120}
              className="mt-14 scroll-mt-32 lg:scroll-mt-36"
            >
              <form
                ref={formCardRef}
                onSubmit={(event) => {
                  event.preventDefault();

                  if (step === 4) {
                    handleSubmit();
                  }
                }}
                className="rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85 sm:p-7 lg:p-8"
              >
                <Stepper step={step} />

                <div className="mt-8">
                  {step === 1 && (
                    <StepIdentity
                      formData={formData}
                      errors={errors}
                      onChange={handleChange}
                      age={age}
                      birthYear={birthYear}
                      onNext={goToNextStep}
                    />
                  )}

                  {step === 2 && (
                    <StepContact
                      formData={formData}
                      errors={errors}
                      onChange={handleChange}
                      onPrevious={goToPreviousStep}
                      onNext={goToNextStep}
                    />
                  )}

                  {step === 3 && (
                    <StepGroup
                      formData={formData}
                      errors={errors}
                      onChange={handleChange}
                      onGroupChange={handleGroupChange}
                      groups={groups}
                      loadingGroups={loadingGroups}
                      selectedGroup={selectedGroup}
                      onPrevious={goToPreviousStep}
                      onNext={goToNextStep}
                    />
                  )}

                  {step === 4 && (
                    <StepSummary
                      formData={formData}
                      selectedGroup={selectedGroup}
                      loadingSubmit={loadingSubmit}
                      onPrevious={goToPreviousStep}
                      onSubmit={handleSubmit}
                    />
                  )}
                </div>
              </form>

              <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-xs leading-6 text-slate-500 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                <div className="flex items-start gap-3">
                  <FaInfoCircle className="mt-1 shrink-0 text-primary" />
                  <p>
                    Les informations recueillies sont nécessaires à la gestion
                    de votre demande d’adhésion au club. Elles ne sont pas
                    revendues et sont utilisées uniquement dans le cadre de la
                    vie associative.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <Footer />
        <ScrollIndicator />
      </main>

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        theme="colored"
      />
    </div>
  );
}
